import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, Plus, Edit, Trash2, ArrowLeft, FileText, ClipboardCheck, Save, Search, CalendarDays, MapPin, Clock, Play, CheckCircle2, Check, ChevronRight, AlertCircle, UserCheck, ListFilter, Lock, Vote, Send, BarChart3, RotateCcw, Loader2 } from 'lucide-react';
import { API, fmtDate, norm } from '@/lib/helpers';
import VotacaoFormDialog, { emptyForm as emptyVotacaoForm } from './VotacaoFormDialog';
import TipTapEditor from './TipTapEditor';

const STATUS_MAP = {
  agendada: { label: 'Agendada', color: 'bg-violet-100 text-violet-700', border: 'border-l-violet-500' },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-100 text-amber-700', border: 'border-l-amber-500' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
};

const FILTROS_REUNIAO = [
  { key: 'todas', label: 'Todas', icon: 'bg-violet-100', iconColor: 'text-violet-600' },
  { key: 'agendada', label: 'Agendadas', icon: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { key: 'em_andamento', label: 'Em Andamento', icon: 'bg-amber-100', iconColor: 'text-amber-600' },
  { key: 'concluida', label: 'Concluídas', icon: 'bg-green-100', iconColor: 'text-green-600' },
];

export default function ReunioesTab({ votacoesEnabled = false }) {
  const [reunioes, setReunioes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todas');

  // Detail view
  const [selectedReuniao, setSelectedReuniao] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Create/Edit dialog
  const [formDialog, setFormDialog] = useState(false);
  const [editingReuniao, setEditingReuniao] = useState(null);
  const [form, setForm] = useState({ titulo: '', data: '', local: '', participantes_admins: true, participantes_auxiliares: true, tipo_evento_id: '' });
  const [saving, setSaving] = useState(false);
  const [tiposEvento, setTiposEvento] = useState([]);

  // Task dialog
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ descricao: '', atribuido_a_user_id: '', prazo: '' });
  const [assignees, setAssignees] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [savingTask, setSavingTask] = useState(false);

  // Pauta dialog
  const [pautaDialog, setPautaDialog] = useState(false);
  const [editingPauta, setEditingPauta] = useState(null);
  const [pautaText, setPautaText] = useState('');
  const [savingPauta, setSavingPauta] = useState(false);

  // Ata
  const [ataContent, setAtaContent] = useState('');
  const [savingAta, setSavingAta] = useState(false);
  const [ataSaved, setAtaSaved] = useState(false);
  const ataTimerRef = useRef(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Presença
  const [pessoas, setPessoas] = useState([]);
  const [presencaSearch, setPresencaSearch] = useState('');
  const [presencaDialog, setPresencaDialog] = useState(false);

  // Votações da reunião
  const [reuniaoVotacoes, setReuniaoVotacoes] = useState([]);
  const [votacaoFormOpen, setVotacaoFormOpen] = useState(false);
  const [detailVotacao, setDetailVotacao] = useState(null);
  const [respostasVotacao, setRespostasVotacao] = useState([]);

  const fetchTiposEvento = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reunioes-tipos-evento`, { credentials: 'include' });
      if (r.ok) setTiposEvento(await r.json());
    } catch (err) { console.warn('Falha ao carregar tipos de evento', err); }
  }, []);

  const fetchReunioes = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reunioes`, { credentials: 'include' });
      if (r.ok) setReunioes(await r.json());
    } catch (err) { console.warn('Falha ao carregar reuniões', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReunioes(); fetchTiposEvento(); }, [fetchReunioes, fetchTiposEvento]);

  const fetchDetail = useCallback(async (reuniaoId) => {
    setLoadingDetail(true);
    try {
      const [r, pR] = await Promise.all([
        fetch(`${API}/reunioes/${reuniaoId}`, { credentials: 'include' }),
        fetch(`${API}/pessoas?status=aprovado`, { credentials: 'include' }),
      ]);
      if (r.ok) {
        const data = await r.json();
        setDetail(data);
        setAtaContent(data.ata_conteudo || '');
      }
      if (pR.ok) {
        const pData = await pR.json();
        setPessoas(Array.isArray(pData) ? pData : (pData.items || pData.pessoas || []));
      }
    } catch { toast.error('Erro ao carregar reunião'); }
    finally { setLoadingDetail(false); }
  }, []);

  const fetchReuniaoVotacoes = useCallback(async (reuniaoId) => {
    try {
      const r = await fetch(`${API}/reunioes/${reuniaoId}/votacoes`, { credentials: 'include' });
      if (r.ok) setReuniaoVotacoes(await r.json());
    } catch (err) { console.warn('Falha ao carregar votações da reunião', err); }
  }, []);

  const openDetail = (reuniao) => {
    setSelectedReuniao(reuniao.reuniao_id);
    fetchDetail(reuniao.reuniao_id);
    fetchReuniaoVotacoes(reuniao.reuniao_id);
  };

  const backToList = () => { setSelectedReuniao(null); setDetail(null); setReuniaoVotacoes([]); fetchReunioes(); };

  // Votação actions
  const handleVotacaoAction = async (votacaoId, action) => {
    try {
      const r = await fetch(`${API}/votacoes/${votacaoId}/${action}`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success(action === 'enviar' ? 'Votação enviada!' : action === 'encerrar' ? 'Votação encerrada!' : 'Votação reaberta!');
      fetchReuniaoVotacoes(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteVotacao = async (votacaoId) => {
    try {
      const r = await fetch(`${API}/votacoes/${votacaoId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Votação excluída!');
      fetchReuniaoVotacoes(detail.reuniao_id);
    } catch { toast.error('Erro ao excluir votação'); }
  };

  const handleViewRespostas = async (votacao) => {
    setDetailVotacao(votacao);
    setRespostasVotacao([]);
    try {
      const r = await fetch(`${API}/votacoes/${votacao.votacao_id}/respostas`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setRespostasVotacao(data.respostas || []);
      }
    } catch { toast.error('Erro ao carregar respostas'); }
  };

  const openCreate = async () => {
    try {
      const r = await fetch(`${API}/reunioes-tipos-evento`, { credentials: 'include' });
      const tipos = r.ok ? await r.json() : [];
      setTiposEvento(tipos);
      if (tipos.length === 0) {
        toast.error('Para criar uma reunião, é necessário ter pelo menos um Tipo de Evento com a opção "Disponível para Reuniões" ativada. Acesse Configurações > Tipos de Evento para configurar.');
        return;
      }
      setEditingReuniao(null);
      const defaultTipo = tipos.length === 1 ? tipos[0].tipo_evento_id : '';
      setForm({ titulo: '', data: '', local: '', participantes_admins: true, participantes_auxiliares: true, tipo_evento_id: defaultTipo });
      setFormDialog(true);
    } catch {
      toast.error('Erro ao verificar tipos de evento.');
    }
  };

  const openEdit = async () => {
    if (!detail) return;
    await fetchTiposEvento();
    setEditingReuniao(detail);
    const dt = detail.data && detail.hora ? `${detail.data}T${detail.hora}` : detail.data || '';
    setForm({
      titulo: detail.titulo, data: dt,
      local: detail.local || '',
      participantes_admins: detail.participantes_admins !== false,
      participantes_auxiliares: detail.participantes_auxiliares !== false,
      tipo_evento_id: detail.tipo_evento_id || '',
    });
    setFormDialog(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error('Título é obrigatório'); return; }
    if (!form.data) { toast.error('Data é obrigatória'); return; }
    setSaving(true);
    try {
      const url = editingReuniao ? `${API}/reunioes/${editingReuniao.reuniao_id}` : `${API}/reunioes`;
      const method = editingReuniao ? 'PUT' : 'POST';
      const [datePart, timePart] = (form.data || '').split('T');
      const payload = { ...form, data: datePart || '', hora: timePart || '', descricao: '' };
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(editingReuniao ? 'Reunião atualizada!' : 'Reunião criada!');
      setFormDialog(false);
      if (editingReuniao) fetchDetail(editingReuniao.reuniao_id);
      else { fetchReunioes(); }
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleStatusAdvance = async () => {
    if (!detail) return;
    const next = detail.status === 'agendada' ? 'Em Andamento' : 'Concluída';
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/status`, { method: 'PUT', credentials: 'include' });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro ao avançar status'); }
      toast.success(`Reunião marcada como ${next}!`);
      fetchDetail(detail.reuniao_id);
      fetchReuniaoVotacoes(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
  };

  const handleStatusRevert = async () => {
    if (!detail) return;
    const prev = detail.status === 'concluida' ? 'Em Andamento' : 'Agendada';
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/status-revert`, { method: 'PUT', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(`Reunião voltou para ${prev}!`);
      fetchDetail(detail.reuniao_id);
      fetchReuniaoVotacoes(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      let url = '';
      if (deleteTarget.type === 'reuniao') url = `${API}/reunioes/${deleteTarget.id}`;
      else if (deleteTarget.type === 'pauta') url = `${API}/reunioes/${detail.reuniao_id}/pautas/${deleteTarget.id}`;
      else if (deleteTarget.type === 'tarefa') url = `${API}/reunioes/${detail.reuniao_id}/tarefas/${deleteTarget.id}`;
      const r = await fetch(url, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Excluído!');
      setDeleteDialog(false);
      setDeleteTarget(null);
      if (deleteTarget.type === 'reuniao') backToList();
      else fetchDetail(detail.reuniao_id);
    } catch { toast.error('Erro ao excluir'); }
  };

  // Pauta
  const handleAddPauta = async () => {
    if (!pautaText.trim()) { toast.error('Texto da pauta é obrigatório'); return; }
    setSavingPauta(true);
    try {
      const url = editingPauta
        ? `${API}/reunioes/${detail.reuniao_id}/pautas/${editingPauta.pauta_id}`
        : `${API}/reunioes/${detail.reuniao_id}/pautas`;
      const r = await fetch(url, {
        method: editingPauta ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ texto: pautaText.trim() }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success(editingPauta ? 'Pauta atualizada!' : 'Pauta adicionada!');
      setPautaDialog(false); setPautaText(''); setEditingPauta(null);
      fetchDetail(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
    finally { setSavingPauta(false); }
  };

  // Ata - auto-save with debounce
  const handleSaveAta = async (content) => {
    setSavingAta(true);
    setAtaSaved(false);
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/ata`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ conteudo: content }),
      });
      if (!r.ok) throw new Error();
      setAtaSaved(true);
      setTimeout(() => setAtaSaved(false), 2000);
    } catch { toast.error('Erro ao salvar ata'); }
    finally { setSavingAta(false); }
  };

  const handleAtaChange = (content) => {
    setAtaContent(content);
    if (ataTimerRef.current) clearTimeout(ataTimerRef.current);
    ataTimerRef.current = setTimeout(() => handleSaveAta(content), 1500);
  };

  // Task
  const fetchAssignees = useCallback(async () => {
    if (!detail?.reuniao_id) return;
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/assignees`, { credentials: 'include' });
      if (r.ok) setAssignees(await r.json());
    } catch (err) { console.warn('Falha ao carregar responsáveis da reunião', err); }
  }, [detail?.reuniao_id]);

  const openTaskDialog = async () => {
    setEditingTask(null);
    setTaskForm({ descricao: '', atribuido_a_user_id: '', prazo: '' });
    setAssigneeSearch('');
    await fetchAssignees();
    setTaskDialog(true);
  };

  const handleAddTask = async () => {
    if (!taskForm.descricao.trim()) { toast.error('Descrição é obrigatória'); return; }
    if (!taskForm.atribuido_a_user_id) { toast.error('Selecione o responsável'); return; }
    setSavingTask(true);
    try {
      const url = editingTask
        ? `${API}/reunioes/${detail.reuniao_id}/tarefas/${editingTask.tarefa_id}`
        : `${API}/reunioes/${detail.reuniao_id}/tarefas`;
      const r = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(taskForm),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success(editingTask ? 'Tarefa atualizada!' : 'Tarefa criada!');
      setTaskDialog(false); setEditingTask(null);
      fetchDetail(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
    finally { setSavingTask(false); }
  };

  const handleToggleTask = async (tarefa) => {
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/tarefas/${tarefa.tarefa_id}/toggle`, { method: 'PUT', credentials: 'include' });
      if (!r.ok) throw new Error();
      fetchDetail(detail.reuniao_id);
    } catch { toast.error('Erro ao atualizar tarefa'); }
  };

  const handleTogglePresenca = async (pessoaId) => {
    const current = detail.presencas?.[pessoaId] || false;
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/presenca`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ pessoa_id: pessoaId, presente: !current }),
      });
      if (!r.ok) throw new Error();
      setDetail(prev => ({ ...prev, presencas: { ...prev.presencas, [pessoaId]: !current } }));
    } catch { toast.error('Erro ao marcar presença'); }
  };

  // Filter
  const counts = {
    todas: reunioes.length,
    agendada: reunioes.filter(r => r.status === 'agendada').length,
    em_andamento: reunioes.filter(r => r.status === 'em_andamento').length,
    concluida: reunioes.filter(r => r.status === 'concluida').length,
  };

  const filtered = reunioes.filter(r => {
    if (filter !== 'todas' && r.status !== filter) return false;
    if (search && !norm(r.titulo).includes(norm(search))) return false;
    return true;
  });

  // ========== DETAIL VIEW ==========
  if (selectedReuniao) {
    if (loadingDetail || !detail) {
      return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }
    const st = STATUS_MAP[detail.status] || STATUS_MAP.agendada;
    return (
      <div data-testid="reuniao-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={backToList} className="bg-white w-fit" data-testid="back-to-list-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{detail.titulo}</h2>
              <Badge className={st.color}>{st.label}</Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400 mt-1 flex-wrap">
              <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(detail.data)}{detail.hora ? ` às ${detail.hora}` : ''}</span>
              {detail.local && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{detail.local}</span>}
            </div>
          </div>
          <div className="flex gap-1 items-center flex-wrap">
            <TooltipProvider delayDuration={200}>
              {detail.status !== 'agendada' && (
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:bg-slate-100" onClick={handleStatusRevert} data-testid="revert-status-btn"><ArrowLeft className="w-4 h-4" /></Button>
                </TooltipTrigger><TooltipContent>{detail.status === 'concluida' ? 'Reabrir reunião' : 'Voltar para Agendada'}</TooltipContent></Tooltip>
              )}

              {detail.status !== 'concluida' && (
                <>
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-cyan-600 hover:bg-cyan-50" onClick={() => { setPresencaSearch(''); setPresencaDialog(true); }} data-testid="manage-presenca-btn"><UserCheck className="w-4 h-4" /></Button>
                  </TooltipTrigger><TooltipContent>Gerenciar Presença ({Object.values(detail.presencas || {}).filter(Boolean).length}/{pessoas.filter(p => p.auxiliar_jovens).length})</TooltipContent></Tooltip>

                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50" onClick={() => {
                      const presentes = Object.values(detail.presencas || {}).filter(Boolean).length;
                      if (presentes === 0) { toast.error('É necessário registrar a presença de pelo menos 1 auxiliar antes de avançar.'); return; }
                      handleStatusAdvance();
                    }} data-testid="advance-status-btn">
                      {detail.status === 'agendada' ? <Play className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger><TooltipContent>{detail.status === 'agendada' ? 'Iniciar reunião' : 'Concluir reunião'}</TooltipContent></Tooltip>

                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-blue-500 hover:bg-blue-50" onClick={openEdit} data-testid="edit-reuniao-btn"><Edit className="w-4 h-4" /></Button>
                  </TooltipTrigger><TooltipContent>Editar reunião</TooltipContent></Tooltip>

                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => { setDeleteTarget({ type: 'reuniao', id: detail.reuniao_id, name: detail.titulo }); setDeleteDialog(true); }} data-testid="delete-reuniao-btn"><Trash2 className="w-4 h-4" /></Button>
                  </TooltipTrigger><TooltipContent>Excluir reunião</TooltipContent></Tooltip>
                </>
              )}
            </TooltipProvider>
          </div>
        </div>

        {/* Grid 2x2: Pauta + Ata (top) | Tarefas + Presença (bottom) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT COLUMN: ATA */}
          <Card className="border-0 shadow-sm overflow-hidden lg:col-span-3 flex flex-col" data-testid="ata-section">
            <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
              <div className="flex items-center gap-2"><Edit className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Ata</span></div>
              {detail.status !== 'concluida' && <Badge className="bg-blue-100 text-blue-600 text-[10px]">{savingAta ? <><Clock className="w-3 h-3 mr-1" />Salvando...</> : <><CheckCircle2 className="w-3 h-3 mr-1" />Salvamento Automático!</>}</Badge>}
            </div>
            <CardContent className="p-0 flex-1 flex flex-col">
              <TipTapEditor
                content={ataContent}
                onChange={handleAtaChange}
                placeholder="Comece a escrever a ata da reunião..."
                readOnly={detail.status === 'concluida'}
                testId="reuniao-ata-richtext-editor"
              />
            </CardContent>
          </Card>

          {/* RIGHT COLUMN: Pauta + Tarefas */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            {/* PAUTA */}
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="pauta-section">
            <div className="bg-violet-50 px-4 py-3 border-b border-violet-100 flex items-center justify-between">
              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-violet-600" /><span className="text-sm font-semibold text-violet-900">Pauta</span></div>
              <span className="text-xs text-violet-500">{detail.pautas?.length || 0} itens</span>
            </div>
            <div>
              {(detail.pautas || []).length > 0 ? (
                <div className="p-3 flex flex-col gap-3">
                  {(detail.pautas || []).map((p, idx) => (
                    <div key={p.pauta_id} className={`flex items-center gap-2 p-2 rounded-lg group bg-slate-50`} data-testid={`pauta-item-${p.pauta_id}`}>
                      <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">{p.texto}</p>
                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                          {p.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(p.created_at)}</span>}
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {p.created_by_role === 'auxiliar'
                              ? <span>{p.created_by_name} (Auxiliar)</span>
                              : <span>{p.created_by_name}</span>}
                          </span>
                        </div>
                      </div>
                      {detail.status !== 'concluida' && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                          <button onClick={() => { setEditingPauta(p); setPautaText(p.texto); setPautaDialog(true); }}
                            className="p-1 hover:bg-slate-200 rounded" data-testid={`edit-pauta-${p.pauta_id}`}>
                            <Edit className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                          <button onClick={() => { setDeleteTarget({ type: 'pauta', id: p.pauta_id, name: p.texto }); setDeleteDialog(true); }}
                            className="p-1 hover:bg-slate-200 rounded" data-testid={`delete-pauta-${p.pauta_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center"><FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma pauta</p></div>
              )}
            </div>
            {detail.status !== 'concluida' && (
              <div className="p-3 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => { setPautaText(''); setEditingPauta(null); setPautaDialog(true); }}
                  className="w-full border-dashed border-violet-200 text-violet-600 hover:bg-violet-50" data-testid="add-pauta-btn">
                  <Plus className="w-4 h-4 mr-1" />Adicionar Pauta
                </Button>
              </div>
            )}
          </Card>

          {/* TAREFAS */}
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="tarefas-section">
            <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center justify-between">
              <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-green-600" /><span className="text-sm font-semibold text-green-900">Tarefas</span></div>
              <span className="text-xs text-green-600">{detail.tarefas_concluidas}/{detail.total_tarefas} concluídas</span>
            </div>
            <div>
              {(detail.tarefas || []).length > 0 ? (
                <div className="p-3 flex flex-col gap-3">
                  {(detail.tarefas || []).map(t => (
                    <div key={t.tarefa_id}
                      className={`flex items-center gap-2 p-2 rounded-lg group cursor-pointer transition-all bg-slate-50 hover:bg-slate-100`}
                      onClick={() => detail.status !== 'concluida' && handleToggleTask(t)}
                      data-testid={`tarefa-item-${t.tarefa_id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0 ${t.concluida ? 'bg-green-500' : ''}`} style={t.concluida ? undefined : { backgroundColor: '#939393' }} title={t.atribuido_a_name}>
                        {t.concluida ? <Check className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">{t.descricao}</p>
                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                          {t.prazo && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtDate(t.prazo)}</span>}
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t.atribuido_a_name}</span>
                          {t.concluida
                            ? <Badge className="bg-green-100 text-green-700 text-[10px]">Concluída</Badge>
                            : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>}
                        </div>
                      </div>
                      {detail.status !== 'concluida' && !t.concluida && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                          <button onClick={async (e) => { e.stopPropagation(); setEditingTask(t); setTaskForm({ descricao: t.descricao, atribuido_a_user_id: t.atribuido_a_user_id, prazo: t.prazo || '' }); setAssigneeSearch(''); await fetchAssignees(); setTaskDialog(true); }}
                            className="p-1 hover:bg-slate-200 rounded" data-testid={`edit-tarefa-${t.tarefa_id}`}>
                            <Edit className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'tarefa', id: t.tarefa_id, name: t.descricao }); setDeleteDialog(true); }}
                            className="p-1 hover:bg-slate-200 rounded" data-testid={`delete-tarefa-${t.tarefa_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center"><ClipboardCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma tarefa</p></div>
              )}
            </div>
            {detail.status !== 'concluida' && (
              <div className="p-3 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={openTaskDialog}
                  className="w-full border-dashed border-green-200 text-green-600 hover:bg-green-50" data-testid="add-tarefa-btn">
                  <Plus className="w-4 h-4 mr-1" />Nova Tarefa
                </Button>
              </div>
            )}
          </Card>

          {/* VOTAÇÕES */}
          {votacoesEnabled && (
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="votacoes-reuniao-section">
            <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex items-center justify-between">
              <div className="flex items-center gap-2"><Vote className="w-4 h-4 text-indigo-600" /><span className="text-sm font-semibold text-indigo-900">Votações</span></div>
              <span className="text-xs text-indigo-500">{reuniaoVotacoes.length} {reuniaoVotacoes.length === 1 ? 'votação' : 'votações'}</span>
            </div>
            <div>
              {reuniaoVotacoes.length > 0 ? (
                <div className="p-3 flex flex-col gap-3">
                  {reuniaoVotacoes.map(v => {
                    const isRascunho = v.status === 'nao_enviada';
                    const isEnviada = v.status === 'enviada' || v.status === 'respondida';
                    const isEncerrada = v.status === 'encerrada';
                    return (
                      <div key={v.votacao_id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 group" data-testid={`votacao-item-${v.votacao_id}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isEncerrada ? 'bg-red-100' : isEnviada ? 'bg-blue-100' : 'bg-slate-200'}`}>
                          <Vote className={`w-3.5 h-3.5 ${isEncerrada ? 'text-red-600' : isEnviada ? 'text-blue-600' : 'text-slate-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800">{v.pergunta}</p>
                          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                            <Badge className={`text-[10px] ${isEncerrada ? 'bg-red-100 text-red-700' : isEnviada ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                              {isEncerrada ? 'Encerrada' : isEnviada ? 'Enviada' : 'Rascunho'}
                            </Badge>
                            <span>{v.total_respostas || 0} resposta{(v.total_respostas || 0) !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        {detail.status === 'concluida' ? (
                          <div className="flex gap-1">
                            {(v.total_respostas || 0) > 0 && (
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <button onClick={() => handleViewRespostas(v)} className="p-1 hover:bg-indigo-100 rounded" data-testid={`respostas-votacao-${v.votacao_id}`}>
                                  <BarChart3 className="w-3.5 h-3.5 text-indigo-500" />
                                </button>
                              </TooltipTrigger><TooltipContent><p>Respostas</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                          </div>
                        ) : (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                          {isRascunho && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <button onClick={() => handleVotacaoAction(v.votacao_id, 'enviar')} className="p-1 hover:bg-blue-100 rounded" data-testid={`enviar-votacao-${v.votacao_id}`}>
                                <Send className="w-3.5 h-3.5 text-blue-500" />
                              </button>
                            </TooltipTrigger><TooltipContent><p>Enviar</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {isEnviada && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <button onClick={() => handleVotacaoAction(v.votacao_id, 'encerrar')} className="p-1 hover:bg-red-100 rounded" data-testid={`encerrar-votacao-${v.votacao_id}`}>
                                <Lock className="w-3.5 h-3.5 text-red-500" />
                              </button>
                            </TooltipTrigger><TooltipContent><p>Encerrar</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {isEncerrada && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <button onClick={() => handleVotacaoAction(v.votacao_id, 'reabrir')} className="p-1 hover:bg-amber-100 rounded" data-testid={`reabrir-votacao-${v.votacao_id}`}>
                                <RotateCcw className="w-3.5 h-3.5 text-amber-500" />
                              </button>
                            </TooltipTrigger><TooltipContent><p>Reabrir</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          {(isEnviada || isEncerrada) && (
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <button onClick={() => handleViewRespostas(v)} className="p-1 hover:bg-indigo-100 rounded" data-testid={`respostas-votacao-${v.votacao_id}`}>
                                <BarChart3 className="w-3.5 h-3.5 text-indigo-500" />
                              </button>
                            </TooltipTrigger><TooltipContent><p>Respostas</p></TooltipContent></Tooltip></TooltipProvider>
                          )}
                          <TooltipProvider><Tooltip><TooltipTrigger asChild>
                            <button onClick={() => handleDeleteVotacao(v.votacao_id)} className="p-1 hover:bg-slate-200 rounded" data-testid={`delete-votacao-${v.votacao_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          </TooltipTrigger><TooltipContent><p>Excluir</p></TooltipContent></Tooltip></TooltipProvider>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-6 text-center"><Vote className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma votação</p></div>
              )}
            </div>
            {detail.status !== 'concluida' && (
              <div className="p-3 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => setVotacaoFormOpen(true)}
                  className="w-full border-dashed border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="add-votacao-btn">
                  <Plus className="w-4 h-4 mr-1" />Nova Votação
                </Button>
              </div>
            )}
          </Card>
          )}
          </div>
        </div>

        {/* VOTAÇÃO FORM DIALOG */}
        <VotacaoFormDialog
          open={votacaoFormOpen}
          onOpenChange={setVotacaoFormOpen}
          reuniaoId={detail?.reuniao_id}
          onSaved={() => fetchReuniaoVotacoes(detail.reuniao_id)}
        />

        {/* VOTAÇÃO RESPOSTAS DIALOG */}
        <Dialog open={!!detailVotacao} onOpenChange={() => setDetailVotacao(null)}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Resultados</DialogTitle>
            </DialogHeader>
            {detailVotacao && (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-900 mb-4">{detailVotacao.pergunta}</p>

                {detailVotacao.tipo === 'multipla_escolha' && detailVotacao.opcoes?.length > 0 && (
                  <div className="space-y-4">
                    {detailVotacao.opcoes.map((op, i) => {
                      const count = respostasVotacao.filter(r => r.opcoes_selecionadas?.includes(i)).length;
                      const total = respostasVotacao.length || 1;
                      const pct = Math.round((count / total) * 100);
                      const isCorreta = detailVotacao.marcar_correta && op.correta;
                      return (
                        <div key={i} className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs text-slate-700 font-medium">{op.texto}</p>
                              {isCorreta && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded">Correta</span>}
                            </div>
                            <span className="text-xs text-slate-500">{count} / {pct}%</span>
                          </div>
                          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${isCorreta ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {detailVotacao.tipo === 'texto' && (
                  <div className="space-y-2">
                    {respostasVotacao.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                          <p className="text-xs text-slate-600 mt-0.5">&ldquo;{r.texto}&rdquo;</p>
                        </div>
                      </div>
                    ))}
                    {respostasVotacao.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">Nenhuma resposta ainda.</p>
                    )}
                  </div>
                )}

                {detailVotacao.tipo === 'multipla_escolha' && respostasVotacao.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Respostas individuais</p>
                    <div className="space-y-2">
                      {respostasVotacao.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              &ldquo;{(r.opcoes_selecionadas || []).map(idx => detailVotacao.opcoes?.[idx]?.texto).filter(Boolean).join(', ')}&rdquo;
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-500 pt-1 border-t border-slate-100">{respostasVotacao.length} {respostasVotacao.length === 1 ? 'resposta' : 'respostas'} no total</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* PRESENÇA DIALOG */}
        <Dialog open={presencaDialog} onOpenChange={setPresencaDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gerenciar Presença</DialogTitle>
            </DialogHeader>
            <div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={presencaSearch} onChange={e => setPresencaSearch(e.target.value)} placeholder="Buscar pessoa..." className="pl-9 h-8 text-sm" data-testid="presenca-search" />
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 pt-3 pb-1">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Auxiliares</span>
                  <span className="text-[11px] text-slate-500">{Object.values(detail.presencas || {}).filter(Boolean).length}/{pessoas.filter(p => p.auxiliar_jovens).length} presentes</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {pessoas
                    .filter(p => p.auxiliar_jovens)
                    .filter(p => !presencaSearch || norm(p.nome_completo || '').includes(norm(presencaSearch)))
                    .map(p => {
                      const presente = detail.presencas?.[p.pessoa_id] || false;
                      return (
                        <div key={p.pessoa_id}
                          className={`flex items-center gap-2 px-4 py-2 cursor-pointer transition-colors ${presente ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                          onClick={() => detail.status !== 'concluida' && handleTogglePresenca(p.pessoa_id)}
                          data-testid={`presenca-${p.pessoa_id}`}>
                          <Checkbox checked={presente} className="data-[state=checked]:bg-green-600" tabIndex={-1} />
                          <span className={`text-sm ${presente ? 'text-green-800 font-medium' : 'text-slate-600'}`}>{p.nome_completo}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* PAUTA DIALOG */}
        <Dialog open={pautaDialog} onOpenChange={v => { if (!v) { setEditingPauta(null); setPautaText(''); } setPautaDialog(v); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingPauta ? 'Editar Pauta' : 'Adicionar Pauta'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Pauta</Label><Textarea rows={3} value={pautaText} onChange={e => setPautaText(e.target.value)} data-testid="pauta-input" /></div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setPautaDialog(false)}>Cancelar</Button>
                <Button onClick={handleAddPauta} disabled={savingPauta} className="bg-violet-600 hover:bg-violet-700" data-testid="save-pauta-btn">{savingPauta ? 'Salvando...' : (editingPauta ? 'Salvar' : 'Adicionar')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* TASK DIALOG */}
        <Dialog open={taskDialog} onOpenChange={v => { if (!v) { setEditingTask(null); setAssigneeSearch(''); } setTaskDialog(v); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Descrição</Label><Input value={taskForm.descricao} onChange={e => setTaskForm({ ...taskForm, descricao: e.target.value })} data-testid="task-descricao-input" /></div>
              <div>
                <Label>Atribuir a</Label>
                <Select value={taskForm.atribuido_a_user_id} onValueChange={v => setTaskForm({ ...taskForm, atribuido_a_user_id: v })} data-testid="assignee-select">
                  <SelectTrigger className="mt-1" data-testid="assignee-select-trigger">
                    <SelectValue placeholder="Selecione o responsável..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assignees.filter(a => a.group === 'admin').length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Administradores</SelectLabel>
                        {assignees.filter(a => a.group === 'admin').map(a => (
                          <SelectItem key={a.id} value={a.id} className="pl-6">{a.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {assignees.filter(a => a.group === 'auxiliar').length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Auxiliares</SelectLabel>
                        {assignees.filter(a => a.group === 'auxiliar').map(a => (
                          <SelectItem key={a.id} value={a.id} className="pl-6">{a.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Prazo (opcional)</Label><Input type="date" value={taskForm.prazo} onChange={e => setTaskForm({ ...taskForm, prazo: e.target.value })} data-testid="task-prazo-input" /></div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancelar</Button>
                <Button onClick={handleAddTask} disabled={savingTask} className="bg-green-600 hover:bg-green-700" data-testid="save-task-btn">{savingTask ? 'Salvando...' : (editingTask ? 'Salvar' : 'Criar Tarefa')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* DELETE DIALOG */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirmar Exclusão</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p>Tem certeza que deseja excluir <strong>{deleteTarget?.name}</strong>?</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => { setDeleteDialog(false); setDeleteTarget(null); }}>Cancelar</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="confirm-delete-btn">Excluir</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* FORM DIALOG (edit from detail view) */}
        <Dialog open={formDialog} onOpenChange={setFormDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingReuniao ? 'Editar Reunião' : 'Nova Reunião'}</DialogTitle></DialogHeader>
            <div>
              <div>
                <label className="text-sm font-semibold text-slate-800 mb-2 block">Título <span className="text-red-500">*</span></label>
                <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} data-testid="form-titulo" />
              </div>
              {tiposEvento.length > 0 && (
                <div className="mt-3">
                  <label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de Reunião</label>
                  <div className={`grid gap-3 ${tiposEvento.length === 1 ? 'grid-cols-1' : tiposEvento.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {tiposEvento.map(t => {
                      const active = form.tipo_evento_id === t.tipo_evento_id;
                      return (
                        <button key={t.tipo_evento_id} type="button"
                          onClick={() => setForm({ ...form, tipo_evento_id: t.tipo_evento_id })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          data-testid={`tipo-reuniao-${t.tipo_evento_id}`}>
                          <div className="flex items-center gap-2">
                            <CalendarDays className={`w-4 h-4 ${active ? 'text-violet-600' : 'text-slate-400'}`} />
                            <span className={`text-sm font-semibold ${active ? 'text-violet-700' : 'text-slate-700'}`}>{t.nome}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-3">
                <label className="text-sm font-semibold text-slate-800 mb-2 block">Visibilidade</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    onClick={() => setForm({ ...form, participantes_admins: true, participantes_auxiliares: false })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${form.participantes_admins && !form.participantes_auxiliares ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid="participantes-privado">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className={`w-4 h-4 ${form.participantes_admins && !form.participantes_auxiliares ? 'text-violet-600' : 'text-slate-400'}`} />
                      <span className={`text-sm font-semibold ${form.participantes_admins && !form.participantes_auxiliares ? 'text-violet-700' : 'text-slate-700'}`}>Privado</span>
                    </div>
                    <p className="text-[11px] text-slate-500">Apenas administradores</p>
                  </button>
                  <button type="button"
                    onClick={() => setForm({ ...form, participantes_admins: true, participantes_auxiliares: true })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${form.participantes_admins && form.participantes_auxiliares ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid="participantes-auxiliares">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className={`w-4 h-4 ${form.participantes_admins && form.participantes_auxiliares ? 'text-violet-600' : 'text-slate-400'}`} />
                      <span className={`text-sm font-semibold ${form.participantes_admins && form.participantes_auxiliares ? 'text-violet-700' : 'text-slate-700'}`}>Auxiliares</span>
                    </div>
                    <p className="text-[11px] text-slate-500">Visível para todos os membros</p>
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <label className="text-sm font-semibold text-slate-800 mb-2 block">Data e Hora <span className="text-red-500">*</span></label>
                <Input type="datetime-local" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} data-testid="form-data" />
              </div>
              <div className="mt-3">
                <label className="text-sm font-semibold text-slate-800 mb-2 block">Local <span className="text-slate-400 font-normal">(opcional)</span></label>
                <Input value={form.local} onChange={e => setForm({ ...form, local: e.target.value })} data-testid="form-local" />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setFormDialog(false)} className="px-5">Cancelar</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 px-6" data-testid="save-reuniao-btn">{saving ? 'Salvando...' : (editingReuniao ? 'Salvar' : 'Criar Reunião')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="reunioes-list-view">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Reuniões</h2>
                  <Badge className="bg-violet-100 text-violet-700 text-sm font-bold">{counts.todas}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="reunioes-menu">
              {FILTROS_REUNIAO.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-reuniao-${f.key}`}
                  className={`w-full lg:w-full min-w-[140px] lg:min-w-0 text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-violet-600 bg-violet-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.icon}`}>
                      <ListFilter className={`w-4 h-4 ${f.iconColor}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-violet-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={openCreate} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="new-reuniao-btn">
                <Plus className="w-4 h-4 mr-1" />Nova Reunião
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS_REUNIAO.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'reunião' : 'reuniões'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="search-reunioes" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="reunioes-list">
              {loading ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{search || filter !== 'todas' ? 'Nenhuma reunião encontrada' : 'Nenhuma reunião criada'}</p>
                </div>
              ) : filtered.map(r => {
                const st = STATUS_MAP[r.status] || STATUS_MAP.agendada;
                return (
                  <div key={r.reuniao_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 cursor-pointer transition-colors"
                    onClick={() => openDetail(r)} data-testid={`reuniao-card-${r.reuniao_id}`}>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 font-medium">{r.titulo}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <CalendarDays className="w-3 h-3" />{fmtDate(r.data)}{r.hora ? ` às ${r.hora}` : ''}
                          </span>
                          <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                          <span className="text-[10px] text-slate-400">{r.total_pautas} {r.total_pautas === 1 ? 'pauta' : 'pautas'}</span>
                          <span className="text-[10px] text-slate-400">{r.tarefas_concluidas}/{r.total_tarefas} tarefas</span>
                          <span className="text-[10px] text-slate-400">{r.presentes || 0}/{r.total_pessoas || 0} presença</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FORM DIALOG */}
      <Dialog open={formDialog} onOpenChange={setFormDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingReuniao ? 'Editar Reunião' : 'Nova Reunião'}</DialogTitle></DialogHeader>
          <div>
            {/* Título */}
            <div>
              <label className="text-sm font-semibold text-slate-800 mb-2 block">Título <span className="text-red-500">*</span></label>
              <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} data-testid="form-titulo" />
            </div>

            {/* Tipo de Reunião - Card Selector */}
            {tiposEvento.length > 0 && (
              <div className="mt-3">
                <label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de Reunião</label>
                <div className={`grid gap-3 ${tiposEvento.length === 1 ? 'grid-cols-1' : tiposEvento.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {tiposEvento.map(t => {
                    const active = form.tipo_evento_id === t.tipo_evento_id;
                    return (
                      <button key={t.tipo_evento_id} type="button"
                        onClick={() => setForm({ ...form, tipo_evento_id: t.tipo_evento_id })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                        data-testid={`tipo-reuniao-${t.tipo_evento_id}`}>
                        <div className="flex items-center gap-2">
                          <CalendarDays className={`w-4 h-4 ${active ? 'text-violet-600' : 'text-slate-400'}`} />
                          <span className={`text-sm font-semibold ${active ? 'text-violet-700' : 'text-slate-700'}`}>{t.nome}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Visibilidade - Card Selector */}
            <div className="mt-3">
              <label className="text-sm font-semibold text-slate-800 mb-2 block">Visibilidade</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button"
                  onClick={() => setForm({ ...form, participantes_admins: true, participantes_auxiliares: false })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${form.participantes_admins && !form.participantes_auxiliares ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid="participantes-privado">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className={`w-4 h-4 ${form.participantes_admins && !form.participantes_auxiliares ? 'text-violet-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-semibold ${form.participantes_admins && !form.participantes_auxiliares ? 'text-violet-700' : 'text-slate-700'}`}>Privado</span>
                  </div>
                  <p className="text-[11px] text-slate-500">Apenas administradores</p>
                </button>
                <button type="button"
                  onClick={() => setForm({ ...form, participantes_admins: true, participantes_auxiliares: true })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${form.participantes_admins && form.participantes_auxiliares ? 'border-violet-600 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid="participantes-auxiliares">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className={`w-4 h-4 ${form.participantes_admins && form.participantes_auxiliares ? 'text-violet-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-semibold ${form.participantes_admins && form.participantes_auxiliares ? 'text-violet-700' : 'text-slate-700'}`}>Auxiliares</span>
                  </div>
                  <p className="text-[11px] text-slate-500">Visível para todos os membros</p>
                </button>
              </div>
            </div>

            {/* Data e Hora */}
            <div className="mt-3">
              <label className="text-sm font-semibold text-slate-800 mb-2 block">Data e Hora <span className="text-red-500">*</span></label>
              <Input type="datetime-local" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} data-testid="form-data" />
            </div>

            {/* Local */}
            <div className="mt-3">
              <label className="text-sm font-semibold text-slate-800 mb-2 block">Local <span className="text-slate-400 font-normal">(opcional)</span></label>
              <Input value={form.local} onChange={e => setForm({ ...form, local: e.target.value })} data-testid="form-local" />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setFormDialog(false)} className="px-5">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 px-6" data-testid="save-reuniao-btn">{saving ? 'Salvando...' : (editingReuniao ? 'Salvar' : 'Criar Reunião')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
